import { requestSaveCda } from '@/api/cdaApi';
import { uploadFile, getFileById } from '@/api/file/fileApi';
import { INIT_NO_OP_NUM, CDA_FILE_TYPE } from '@/config';
import { setCda, clearCda } from '@/mutation-types';
import kinds from '@/constants/kinds';
import { useChartCdaStagedItemsStore, ReviewChangeNotSaved } from '@/stores/chartCdaStagedItems';
// The side bar's getters order measures with `_.orderBy`, which main's module
// used without importing. This build provides no global `_` (no ProvidePlugin,
// no script tag), so a C-CDA with a vital or lab result threw a ReferenceError
// instead of listing it.
import _ from 'lodash';
import {
  isVitalMeasureForCda,
  resolveProcedureMeasuresForSidebar,
  resolveMeasureSidebarDate,
} from '@/utils/labClassification';

/**
 * A stored file's key as the API addresses it: its 32-hex uid, kept as the
 * string it is, or a numeric id from before the uid. `parseInt` read a uid's
 * leading digits as an id — `'12ab…'` became 12, which names a different
 * document of the same organization — so it is never applied to a uid.
 */
function storedFileKey(value) {
  const text = String(value ?? '').trim();

  if (/^[a-f0-9]{32}$/i.test(text)) return text.toLowerCase();

  return /^\d+$/.test(text) ? Number(text) : INIT_NO_OP_NUM;
}

/**
 * The open chart's C-CDAs: the `login/getAllData` overlay
 * (stores/chartCdas.js). pacmny-be built them into `phrData`; the port's
 * bootstrap carries none, so a getter reading `phrData.cdas` saw nothing.
 */
function chartCdas(rootGetters) {
  const allData = rootGetters && rootGetters['login/getAllData'];

  return (allData && Array.isArray(allData.cdas)) ? allData.cdas : [];
}

/**
 * pacmny-be's code catalogue (`phrData.ccds`), which labelled main's rows. The
 * port ships none — its rows carry their own label — so it is an empty map,
 * not undefined: the side bar's getters look codes up in it unguarded.
 */
function catalogue(rootState) {
  const phrData = rootState && rootState.login && rootState.login.phrData;

  return (phrData && phrData.ccds) || {};
}

/** The C-CDA open on the page, its row from the overlay. */
function openCda(state, rootGetters) {
  if (state.cdaIndex === INIT_NO_OP_NUM) return null;

  return chartCdas(rootGetters)[state.cdaIndex] || null;
}

const state = {
  cdaId: INIT_NO_OP_NUM,    // id in db
  cdaIndex: INIT_NO_OP_NUM, // id in dropdown list
};

const getters = {
  getCdaId: state => state.cdaId,
  getCdaIndex: state => state.cdaIndex,
  /**
   * Groups cda side bar content by date and kind
   * @returns {object} {2018-10-11: {'procedure':[], 'measures':[], 'diagnosis': []...}, }
   */
  getCdaSideBarContentGrouped (state, getters, rootState, rootGetters) {

    if (getters.getCdaIndex === INIT_NO_OP_NUM) return;

    const cda = chartCdas(rootGetters)[state.cdaIndex];
    if (!cda) return;
    const ccds = catalogue(rootState);
    // A saved C-CDA's items are the chart's own (pacmny-be's model).
    const chartModel = (rootState.login && rootState.login.phrData) || {};
    const bdaks = cda.byDateAndKind;
    const measures = (cda.isSaved)?chartModel.measures:cda.emeasures;
    const procedures = (cda.isSaved)?chartModel.procedures:cda.eprocedures;
    const procmeas = (cda.isSaved)?chartModel.procmeas:cda.eprocmeas;
    const diagnosiss = (cda.isSaved)?chartModel.diagnosiss:cda.ediagnosiss;
    const vaccines = (cda.isSaved)?chartModel.vaccines:cda.evaccines;
    const medications = (cda.isSaved)?chartModel.medications:cda.emeds;
    const allergies = (cda.isSaved)?chartModel.allergies:cda.eallergies;
    // Lab vs vitals bucketing uses shared classification (see utils/labClassification.js).
    const vitalCcds = (rootState.login.watch && rootState.login.watch.vitalSignCodes) || [];

    let sideBarContent = {};
    Object.keys(bdaks).forEach(k => {
      let bdak = bdaks[k];
      if (bdak.lo === null || typeof bdak.lo === 'undefined' ) {
        bdak.lo = 'Undated';
      }
      if (sideBarContent[bdak.lo] === null || typeof sideBarContent[bdak.lo] === 'undefined') {
        sideBarContent[bdak.lo] = {};
      }

      const loBucket = sideBarContent[bdak.lo];
      let hasCcd = false;
      let entry = { kind: bdak.kind };

      if (bdak.kind === kinds.E_PROCEDURE || bdak.kind === kinds.PROCEDURE) {
        const ccdId = procedures[bdak.id].ccd;
        if (ccdId) {
          const measureKind = bdak.kind === kinds.PROCEDURE ? kinds.MEASURE : kinds.E_MEASURE;
          const labKind = bdak.kind === kinds.PROCEDURE ? kinds.LAB : kinds.E_LAB;
          const measIds = procmeas[bdak.id] || [];
          const { vitalMeasures, hideProcedure } = resolveProcedureMeasuresForSidebar({
            measIds,
            measures,
            procCcd: ccdId,
            procKind: bdak.kind,
            measureKind,
            lo: bdak.lo,
            ccds,
            vitalSignCodes: vitalCcds,
            labKind,
            sideBarContent,
            flatBuckets: true,
            procedureMeta: procedures[bdak.id],
            cdaIndex: state.cdaIndex,
            procmeas,
            procedures,
          });
          if (hideProcedure) {
            return;
          }
          hasCcd = true;
          entry = {
            id: bdak.id,
            cdaIndex: state.cdaIndex,
            ccd: ccdId,
            desc: (ccds[ccdId] || {}).desc || String(ccdId),
            kind: bdak.kind,
            ...procedures[bdak.id],
            measures: vitalMeasures,
          };
        }
      } else if (bdak.kind === kinds.E_MEASURE || bdak.kind === kinds.MEASURE) {
        const ccdId = measures[bdak.id].ccd;
        if (ccdId) {
          hasCcd = true;
          const labKind = bdak.kind === kinds.MEASURE ? kinds.LAB : kinds.E_LAB;
          const meas = measures[bdak.id];
          const bucketKind = isVitalMeasureForCda(meas, vitalCcds) ? bdak.kind : labKind;
          const measLo = resolveMeasureSidebarDate(meas, bdak.id, {
            procmeas,
            procedures,
            measures,
            ccds,
            fallbackLo: bdak.lo,
          });
          entry = {
            dname: (ccds[ccdId] || {}).desc || String(ccdId),
            kind: bdak.kind,
            ...meas,
            lo: measLo,
            weight: meas.weight ? meas.weight : 0,
          };
          if (!sideBarContent[measLo]) sideBarContent[measLo] = {};
          if (!sideBarContent[measLo][bucketKind]) sideBarContent[measLo][bucketKind] = [];
          sideBarContent[measLo][bucketKind].push(entry);
          if (bucketKind === bdak.kind) {
            sideBarContent[measLo][bucketKind] = _.orderBy(sideBarContent[measLo][bucketKind], 'weight', 'desc');
          }
          hasCcd = false;
        }
      } else if (bdak.kind === kinds.E_DIAGNOSIS || bdak.kind === kinds.DIAGNOSIS) {
        // for the 0 in codes[0], because of icd9,10, different codes have same/similar desc; choose first entry
        let ccdId = diagnosiss[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
          entry = {...entry, ...diagnosiss[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_MEDICATION || bdak.kind === kinds.MEDICATION) {
        let ccdId = medications[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
          entry = {...entry, ...medications[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_VACCINE || bdak.kind === kinds.VACCINE) {
        let ccdId = bdak.kind === kinds.VACCINE?vaccines[bdak.id].codes[0]:vaccines[bdak.id].ccd;
        if (ccdId) {
          hasCcd = true;
          entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
          entry = {...entry, ...vaccines[bdak.id]};
        }
      } else if (bdak.kind === kinds.E_ALLERGY || bdak.kind === kinds.ALLERGY) {
        let ccdId = allergies[bdak.id].codes[0];
        if (ccdId) {
          hasCcd = true;
          entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
          entry = {...entry, ...allergies[bdak.id]};
        }
      }
      if (hasCcd) {
        if (!loBucket[bdak.kind]) {
          loBucket[bdak.kind] = [];
        }
        loBucket[bdak.kind].push(entry);
      }
    });
    return sideBarContent;
  },
  getCdaSideBarContentKindAndDate (state, getters, rootState, rootGetters) {
    if (getters.getCdaIndex === INIT_NO_OP_NUM) return;

    const cda = chartCdas(rootGetters)[state.cdaIndex];
    if (!cda) return;
    const ccds = catalogue(rootState);
    // A saved C-CDA's items are the chart's own (pacmny-be's model).
    const chartModel = (rootState.login && rootState.login.phrData) || {};
    const bkads = cda.byKindAndDate;
    const measures = (cda.isSaved)?chartModel.measures:cda.emeasures;
    const procedures = (cda.isSaved)?chartModel.procedures:cda.eprocedures;
    const procmeas = (cda.isSaved)?chartModel.procmeas:cda.eprocmeas;
    const diagnosiss = (cda.isSaved)?chartModel.diagnosiss:cda.ediagnosiss;
    const vaccines = (cda.isSaved)?chartModel.vaccines:cda.evaccines;
    const medications = (cda.isSaved)?chartModel.medications:cda.emeds;
    const allergies = (cda.isSaved)?chartModel.allergies:cda.eallergies;

    // Vital-sign CCDs (LA_labccd.ZV_labccd_vitalsign = true) loaded via requestVitalSignsCodes.
    // Measures whose CCD is not a vital sign are lab results and belong under "Labs".
    // If the codes have not loaded yet, fall back to treating every measure as a vital
    // so nothing is mislabeled while the list is empty.
    // Lab vs vitals bucketing uses shared classification (see utils/labClassification.js).
    const vitalCcds = (rootState.login.watch && rootState.login.watch.vitalSignCodes) || [];

    let sideBarContent = {};
    let entry = {};
    Object.keys(bkads).forEach(kind => {
      let bkad = bkads[kind];
      sideBarContent[kind]= {};

      entry = {};
      entry.kind = kind;
      sideBarContent.kind = {};

      if (kind === kinds.E_PROCEDURE || kind === kinds.PROCEDURE) {

        bkad.forEach(item => {
          const ccdId = procedures[item.id].ccd;
          if (ccdId) {
            const lo = item.lo || 'Undated';
            const measureKind = kind === kinds.PROCEDURE ? kinds.MEASURE : kinds.E_MEASURE;
            const labKind = kind === kinds.PROCEDURE ? kinds.LAB : kinds.E_LAB;
            const measIds = procmeas[item.id] || [];
            const { vitalMeasures, hideProcedure } = resolveProcedureMeasuresForSidebar({
              measIds,
              measures,
              procCcd: ccdId,
              procKind: kind,
              measureKind,
              lo,
              ccds,
              vitalSignCodes: vitalCcds,
              labKind,
              sideBarContent,
              flatBuckets: false,
              procedureMeta: procedures[item.id],
              cdaIndex: state.cdaIndex,
              procmeas,
              procedures,
            });
            if (hideProcedure) {
              return;
            }
            entry = {
              id: item.id,
              cdaIndex: state.cdaIndex,
              ccd: ccdId,
              desc: (ccds[ccdId] || {}).desc || String(ccdId),
              lo,
              kind,
              ...procedures[item.id],
              measures: vitalMeasures,
            };

            if (!sideBarContent[kind][entry.lo]) {
              sideBarContent[kind][entry.lo] = [];
            }
            sideBarContent[kind][entry.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_MEASURE || kind === kinds.MEASURE) {
        const labKind = (kind === kinds.MEASURE) ? kinds.LAB : kinds.E_LAB;
        bkad.forEach(item => {
          let ccdId = measures[item.id].ccd;
          if (ccdId) {
            const meas = measures[item.id];
            const bucketKind = isVitalMeasureForCda(meas, vitalCcds) ? kind : labKind;
            const measLo = resolveMeasureSidebarDate(meas, item.id, {
              procmeas,
              procedures,
              measures,
              ccds,
              fallbackLo: item.lo || 'Undated',
            });
            entry = {
              dname: (ccds[ccdId] || {}).desc || String(ccdId),
              kind,
              ...meas,
              lo: measLo,
              weight: meas.weight ? meas.weight : 0,
            };
            if (!sideBarContent[bucketKind])
              sideBarContent[bucketKind] = {};
            if (!sideBarContent[bucketKind][measLo])
              sideBarContent[bucketKind][measLo] = [];
            sideBarContent[bucketKind][measLo].push(entry);
            sideBarContent[bucketKind][measLo] = _.orderBy(sideBarContent[bucketKind][measLo], 'weight', 'desc');
          }
        });

      }
      else if (kind === kinds.E_DIAGNOSIS || kind === kinds.DIAGNOSIS) {
        bkad.forEach(item => {
          let ccdId = diagnosiss[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
            entry = {...entry, ...diagnosiss[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_MEDICATION || kind === kinds.MEDICATION) {
        bkad.forEach(item => {
          let ccdId = medications[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = ccds[ccdId] ? (ccds[ccdId] || {}).desc || String(ccdId) : ccdId;
            entry = {...entry, ...medications[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_VACCINE || kind === kinds.VACCINE) {
        bkad.forEach(item => {
          let ccdId = item.kind === kinds.VACCINE?vaccines[item.id].codes[0]:vaccines[item.id].ccd;
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
            entry = {...entry, ...vaccines[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }
      else if (kind === kinds.E_ALLERGY || kind === kinds.ALLERGY) {
        bkad.forEach(item => {
          let ccdId = allergies[item.id].codes[0];
          if (ccdId) {
            entry = {};
            item.lo = item.lo || 'Undated';
            entry.kind = kind;
            entry.desc = (ccds[ccdId] || {}).desc || String(ccdId);
            entry = {...entry, ...allergies[item.id]};
            if (!sideBarContent[kind][item.lo])
              sideBarContent[kind][item.lo] = [];
            sideBarContent[kind][item.lo].push(entry);
          }
        });
      }

    });

    return sideBarContent;

  },
  /**
   * returns a list of all cda ids
   */
  getCdaList (state, getters, rootState, rootGetters) {
    // pick id and recordedAt from all data
    let cdaList = [];
    if (rootGetters['login/getAllData'] &&  rootGetters['login/getAllData'].cdas) {
      rootGetters['login/getAllData'].cdas.forEach((cda,index) => {
        cdaList.push({id:cda.id, index, recordedAt: cda.recordedAt, displayName: (cda.name || cda.recordedAt)});
      });
    }
    return cdaList;
  },
  // The newest: the list is oldest first, as main's (FileUpload.vue opens it
  // after an upload).
  getIndexOfLastCda (state, getters, rootState, rootGetters) {
    return chartCdas(rootGetters).length - 1;
  },
  /** The chart's C-CDAs could not be read: not the same as it having none. */
  cdaListLoadFailed (state, getters, rootState, rootGetters) {
    const allData = rootGetters['login/getAllData'];
    return Boolean(allData && allData.cdasLoadFailed);
  },
  /**
   * The chart's list of C-CDAs has been read (or its read failed). Until then
   * `getCdaList` is empty because nothing has been read, not because the chart
   * has no C-CDAs: pacmny-be shipped the list in the session blob, the port
   * reads it after the page renders (stores/chartCdas.js).
   */
  isCdaListLoaded (state, getters, rootState, rootGetters) {
    const allData = rootGetters['login/getAllData'];
    return Boolean(allData && Array.isArray(allData.cdas));
  },
  getCdaById (state, getters, rootState) {

  },
  isCdaSaved (state, getters, rootState, rootGetters) {
    if (state.cdaIndex === INIT_NO_OP_NUM) return false;
    const cdas = chartCdas(rootGetters);
    if (state.cdaIndex < 0 || state.cdaIndex >= cdas.length) {
      return false;
    }
    const cda = cdas[state.cdaIndex];
    return !!(cda && cda.isSaved);
  },
  /**
   * The open C-CDA's extracted items are in the side bar for the clinician to
   * review. Save CDA commits them into the chart, so it is offered only when
   * this holds; the port's rows say so in `extractionShown`, which is false
   * until the open C-CDA's staged items are read and laid over its row
   * (stores/chartCdas.js, clinicalBridge.withStagedItems).
   */
  isCdaExtractionShown (state, getters, rootState, rootGetters) {
    const cda = openCda(state, rootGetters);
    return Boolean(cda && cda.extractionShown === true);
  },
  /** The open C-CDA's import state: `uploaded`, `scanning`, `staged` or `committed`. */
  getOpenCdaStatus (state, getters, rootState, rootGetters) {
    const cda = openCda(state, rootGetters);
    return (cda && cda.status) || null;
  },
  /**
   * The review's notes on the open C-CDA's extracted items: the ones the
   * document left undated (`missing_date`) or uncoded (`uncoded`), each
   * `{kind, id, field, reason, label}`. They flag; they do not hold Save back.
   */
  getOpenCdaReviewNotes (state, getters, rootState, rootGetters) {
    const cda = openCda(state, rootGetters);
    return (cda && Array.isArray(cda.reviewNotes)) ? cda.reviewNotes : [];
  },
  /**
   * What the scan could not stage from the open C-CDA at all: `{kind, reason,
   * section, label, code}` each, listed apart from the items because there is no
   * item to hold them.
   */
  getOpenCdaNotStaged (state, getters, rootState, rootGetters) {
    const cda = openCda(state, rootGetters);
    return (cda && Array.isArray(cda.notStaged)) ? cda.notStaged : [];
  },
  /**
   * Why Save CDA is not offered for the open C-CDA, in words for the page; null
   * when it is. Save commits the extracted items into the chart, so it waits
   * until they are in the side bar for review (`isCdaExtractionShown`).
   * **Nothing the document left out stops it** (D6 default 2026-10-21): an item
   * with no date or no code known to the vocabulary is a note on the item in the
   * review (`getOpenCdaReviewNotes`), and Save commits it as main's does - an
   * undated item with no date, an uncoded one as an uncoded chart row. The
   * clinician may correct the date or leave the item out first; none of it is a
   * reason to be unable to save a scanned C-CDA into the chart.
   * With no row for the open C-CDA (the list's re-read failed, or it was
   * deleted elsewhere) nothing shows what Save would commit, so it is not
   * offered either. (The API takes a commit only from a `staged` C-CDA,
   * 409 `cda_not_staged`; this is the page saying so before it is asked.)
   */
  getCdaSaveUnavailableReason (state, getters, rootState, rootGetters) {
    if (state.cdaIndex === INIT_NO_OP_NUM) return 'This CDA is still opening.';

    const cda = openCda(state, rootGetters);

    if (!cda) {
      return 'This CDA is not in this chart\'s list of CDAs right now, so it cannot be saved from here. Reload the page to try again.';
    }
    if (cda.isSaved) return null;

    if (cda.status === 'scanning') {
      return 'This CDA is still being read. Its extracted items will appear here for review when it is done.';
    }
    // `uploaded` is a C-CDA whose draft was discarded (DELETE staged-items):
    // nothing is reading it, so nothing will appear by waiting.
    if (cda.status === 'uploaded') {
      return 'This CDA has not been read, so there are no extracted items to review and nothing to save.';
    }
    if (cda.extractionFailed) {
      return 'The items extracted from this CDA could not be loaded for review, so it cannot be saved to the chart. Open it again to retry.';
    }
    if (cda.extractionShown !== true) {
      return 'Saving this CDA to the chart is not available yet: its extracted items are not shown here for review.';
    }
    return null;
  }
};

const mutations = {
  [setCda] (state, payload) {
    state.cdaId = storedFileKey(payload.cdaId);
    state.cdaIndex = parseInt(payload.cdaIndex);
  },
  [clearCda] (state) {
    state.cdaId = INIT_NO_OP_NUM;
    state.cdaIndex = INIT_NO_OP_NUM;
  }
};

const actions = {

  uploadCda ({commit, state, rootState}, formData) {
    return new Promise((resolve, reject) => {
      if (formData) {
        uploadFile(CDA_FILE_TYPE, formData)
            .then((response) => {
              const responseData = response.data;
              // update model
              resolve(responseData.id);
            })
            .catch((err) => {
              reject(err);
            });
      } else {
        reject('Please include a CDA file (in .xml format) you want to upload');
      }
    });
  },

  /**
   * `isCurrent`, when given, says whether the page still wants this C-CDA when
   * its bytes arrive. An answer it no longer wants (the page moved to another
   * C-CDA, or closed, while this one was on its way) does not open it: arriving
   * after the C-CDA now on screen, it would have left `cdaId` naming the
   * earlier one, and Save and the side bar act on `cdaId`.
   */
  requestCdaById ({commit}, reqData) {
    const {id, index, isCurrent} = reqData;
    return new Promise((resolve, reject) => {
      getFileById(CDA_FILE_TYPE, id)
          .then((cdaDoc) => {
            resolve(cdaDoc);
            if (typeof isCurrent !== 'function' || isCurrent()) {
              commit(setCda, {cdaId: id, cdaIndex: index});
            }
          })
          .catch((err) => {
            reject(err);
          });
    });
  },

  /**
   * Commit the C-CDA's extracted items into the chart.
   *
   * **It first waits for every review change on the document that is in the air**
   * (a date PATCH, an item being left out, a date waiting its turn behind one):
   * the commit takes what the API holds, and a change not yet answered is not in
   * it - the old date was committed, or the item the clinician had just left out.
   * If one of those changes failed, nothing is committed: the clinician's change
   * is not on the item, and Save would say it was. The rejection is
   * `ReviewChangeNotSaved` (`code` `review_change_failed`), which the page words;
   * the failure itself is already on the notes (`errors`). Asked again with
   * nothing in the air, Save goes ahead as it stands.
   */
  saveCdaById ({commit, dispatch}, id) {
    return useChartCdaStagedItemsStore().settled(storedFileKey(id)).then((changesWorked) => {
      if (!changesWorked) return Promise.reject(new ReviewChangeNotSaved());

      return new Promise((resolve, reject) => {
        requestSaveCda(id)
        .then(async (response)=> {
          const responseData = response.data;
          // pacmny-be answered the commit with the rebuilt getAllData. The port's
          // commit answers only that it committed, and committing `undefined` as
          // the model emptied it, so the model and the chart's C-CDA list (whose
          // `isSaved` the Save button reads) are read again instead.
          if (responseData && responseData.data) {
            commit('login/setApplicationModel', responseData.data, {root: true});
          } else {
            await dispatch('login/updateApplicationModel', {}, {root: true});
          }
          resolve();
        })
        .catch((err) => {
          reject(err);
        });
      });
    });
  },

  clearCda ({commit}) {
    return new Promise((resolve) => {
      commit(clearCda);
      resolve();
    });
  },

  // --- the review of the open C-CDA's extracted items ---------------------
  //
  // What a clinician does about an item the document left undated or uncoded,
  // before Save (D6 default 2026-10-21). The API state is the Pinia store's
  // (stores/chartCdaStagedItems); these add what only this module knows - the
  // open C-CDA's uid - and read the chart's list again where a write changed a
  // document's status. Each resolves to whether the change was made; a failure
  // is said by the store (`errors`), not thrown.

  /** Correct one item: `changes` is its columns, e.g. `{onset_at: '2026-03-04'}`; `kind` is the set's own name for it. */
  correctStagedItem ({state}, {kind, id, changes}) {
    return useChartCdaStagedItemsStore().correct(state.cdaId, {[kind]: [{id, ...changes}]});
  },

  /** Leave one item out of the chart. If it was the last of a partly saved C-CDA, that C-CDA now reads as saved. */
  async dropStagedItem ({state, dispatch}, {kind, id}) {
    const dropped = await useChartCdaStagedItemsStore().drop(state.cdaId, kind, id);

    if (dropped) await dispatch('login/updateApplicationModel', {}, {root: true});

    return dropped;
  },

  /** Discard every extracted item. The C-CDA is then `uploaded` (or saved, if part of it was), and may be read again. */
  async discardStagedItems ({state, dispatch}) {
    const discarded = await useChartCdaStagedItemsStore().discardAll(state.cdaId);

    if (discarded) await dispatch('login/updateApplicationModel', {}, {root: true});

    return discarded;
  },

  /** Read the open C-CDA again: it is `scanning` until its new set of items is staged. */
  async readCdaAgain ({state, dispatch}) {
    const queued = await useChartCdaStagedItemsStore().rescan(state.cdaId);

    if (queued) await dispatch('login/updateApplicationModel', {}, {root: true});

    return queued;
  },
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
}
